import React, { Fragment, useEffect, useReducer, useState } from 'react';
import { ActivationStatus, FetchType, SortField } from '../../../../../../enums';
import { IManagePersonDetailsLayoutProps } from '../manage-person-panels.models';
import { PagePath } from '../../../../../../navigation/navigation.enums';
import { Department, Team } from '../../../../../../models';
import { useAppActions, useAppState } from '../../../../../../overmind';
import { useForm } from 'react-hook-form';
import * as store from '../manage-person-panels.store';
import { Button, DropdownOption, DropdownSearch, DropdownAsync, DropdownSelect, DropdownSelectOption, EmailValidator, FieldController, RequiredValidator, TextField } from '@keplerco/core';
import { LANGUAGES } from '../../../../../../library/consts/languages';
import { PeriodOfExperience, PeriodOfExperienceDescription } from '../../../../../../enums/period-of-experience';
import { FlagIcon } from '../../../../../../design/icons/flag.icon';
import { Country } from '../../../../../onboarding/classic/classic.onboarding.layout.models';
import { SaveUserDto } from '../../../../../../models/view/employee';

const periodsOfExperience: DropdownSelectOption[] = [
  { label: PeriodOfExperienceDescription.ZeroToTwo, value: PeriodOfExperience.ZeroToTwo.toString() },
  { label: PeriodOfExperienceDescription.TwoToFour, value: PeriodOfExperience.TwoToFour.toString() },
  { label: PeriodOfExperienceDescription.FourToSeven, value: PeriodOfExperience.FourToSeven.toString() },
  { label: PeriodOfExperienceDescription.SevenPlus, value: PeriodOfExperience.SevenPlus.toString() },
];
const languages: DropdownSelectOption[] = LANGUAGES.map(language => ({ value: language }));

function stringToArray(string: string | undefined): string[] {
  if (!string) return [];
  return string.split(',');
}

export function ManagePersonDetailsLayout({ person, onSubmit }: IManagePersonDetailsLayoutProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [, dispatch] = useReducer(store.reducer, store.initialState);

  const { control, handleSubmit, reset, setValue } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const [departments, setDepartments] = useState<Department[]>();
  const [teams, setTeams] = useState<Team[]>();
  const [countries, setCountries] = useState<Country[]>();
  const [departmentSlug, setDepartmentSlug] = useState<string>();
  const [roles, setRoles] = useState<DropdownOption[]>([]);
  const [managers, setManagers] = useState<DropdownOption[]>([]);
  const [isRoleSearching, setsRoleISearching] = useState<boolean>(false);
  const [selectedRoleOptions, setSelectedRoleOptions] = useState<DropdownOption[]>([]);
  const [selectedManagerOptions, setSelectedManagerOptions] = useState<DropdownOption[]>([]);
  const [isManagerSearching, setIsManagerSearching] = useState<boolean>(false);

  async function handleRoleSearch(query: string) {
    setsRoleISearching(true);
    const response = await actions.getCompanyRoles({
      companySlug: companyVariables.slug!,
      search: query,
      sortAscending: true,
      page: 1,
      pageSize: 10,
      searchGlobally: false,
    });
    if (response?.roles !== undefined) {
      setRoles(
        response.roles.map(role => ({
          label: role.name,
          value: role.slug!,
        }))
      );
    } else {
      setRoles([]);
    }
    setsRoleISearching(false);
  }

  async function handleManagerSearch(query: string) {
    setIsManagerSearching(true);
    const response = await actions.searchPeople({
      companySlug: companyVariables.slug!,
      search: query,
      page: 1,
      pageSize: 5,
      sortField: SortField.Name,
      activationStatuses: [ActivationStatus.Active, ActivationStatus.Inactive, ActivationStatus.NotInvited],
      sortAscending: true,
    });
    if (response?.employees !== undefined) {
      setManagers(
        response?.employees?.map(manager => ({
          label: `${manager.firstName} ${manager.lastName}`,
          value: manager.learnerSlug!,
        }))
      );
    } else {
      setManagers([]);
    }
    setIsManagerSearching(false);
  }

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: PagePath.userManagementPeople, type: FetchType.DialogFetching });

      const [departmentsResponse, teamsResponse, countriesResponse] = await Promise.all([actions.getDepartments(companyVariables.slug!), actions.getTeams(companyVariables.slug!), actions.getCountries()]);

      setDepartments(departmentsResponse?.sort((a, b) => a.name.localeCompare(b.name)));
      setTeams(teamsResponse?.sort((a, b) => a.teamName.localeCompare(b.teamName)));
      setCountries(countriesResponse?.sort((a, b) => a.name.localeCompare(b.name)));

      if (!!person) {
        reset({
          firstName: person.firstName,
          lastName: person.lastName,
          email: person.email,
          jobTitle: person.jobTitle,
          periodOfExperience: person.periodOfExperience?.toString(),
          departmentSlug: person.departmentSlug,
          teamSlug: person.teamSlug,
          countryId: person.countryId?.toString(),
          languages: person.languages?.join(','),
          roleSlugs: person.roles?.map(role => role.slug)?.join(','),
          managerSlugs: person.managers?.map(manager => manager.slug)?.join(','),
        });
        setDepartmentSlug(person.departmentSlug);
        setSelectedRoleOptions(
          person.roles.map(role => ({
            label: role.name,
            value: role.slug!,
          }))
        );

        setSelectedManagerOptions(
          person.managers.map(manager => ({
            label: manager.name,
            value: manager.slug!,
          }))
        );
      }

      actions.stopLoader(PagePath.userManagementPeople);
    }

    getData();
  }, [person]);

  return (
    <div className="card">
      <h4>User Details</h4>

      <form
        id="savePerson"
        onSubmit={handleSubmit(async fieldValues => {
          actions.startLoader({ path: PagePath.userManagementPeople, type: FetchType.Sending });
          const request: SaveUserDto = {
            id: person?.id,
            learnerSlug: person?.learnerSlug,
            firstName: fieldValues.firstName,
            lastName: fieldValues.lastName,
            email: fieldValues.email,
            jobTitle: fieldValues.jobTitle,
            periodOfExperience: parseInt(fieldValues.periodOfExperience),
            departmentSlug: fieldValues.departmentSlug,
            teamSlug: fieldValues.teamSlug,
            countryId: parseInt(fieldValues.countryId),
            languages: stringToArray(fieldValues.languages),
            roleSlugs: stringToArray(fieldValues.roleSlugs),
            managerSlugs: stringToArray(fieldValues.managerSlugs),
          };

          const response = await actions.savePerson(request);

          if (!!response) {
            dispatch({ type: store.ManagePersonPermissionsActionTypes.SetPerson, payload: person });
            onSubmit(response);
          }

          actions.stopLoader(PagePath.userManagementPeople);
        })}
      >
        <div className="row">
          <div className="column">
            <FieldController
              control={control}
              name="firstName"
              rules={new RequiredValidator('Enter a first name')}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="First name"
                  type="text"
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: fieldState.invalid,
                    message: fieldState.error?.message ?? 'Please enter a first name',
                  }}
                  responsive
                />
              )}
            />
          </div>

          <div className="column">
            <FieldController
              control={control}
              name="lastName"
              rules={new RequiredValidator('Enter a last name')}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Last name"
                  type="text"
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: fieldState.invalid,
                    message: fieldState.error?.message ?? 'Please enter a last name',
                  }}
                  responsive
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="column">
            <FieldController
              control={control}
              name="email"
              rules={new EmailValidator()}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: fieldState.invalid,
                    message: fieldState.error?.message ?? 'Please enter an email',
                  }}
                  responsive
                />
              )}
            />
          </div>

          <div className="column">
            <FieldController
              control={control}
              name="jobTitle"
              rules={new RequiredValidator()}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Job title"
                  type="text"
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: fieldState.invalid,
                    message: fieldState.error?.message ?? 'Please enter a job title',
                  }}
                  responsive
                />
              )}
            />
          </div>
        </div>

        <FieldController
          control={control}
          name="periodOfExperience"
          rules={new RequiredValidator()}
          render={({ field, fieldState }) => (
            <DropdownSelect
              {...field}
              label="Years of experience"
              options={periodsOfExperience}
              validation={{
                dirty: fieldState.isDirty || !!fieldState.error,
                invalid: fieldState.invalid,
                message: fieldState.error?.message ?? 'Please select years of experience',
              }}
              responsive
            />
          )}
        />

        <div className="row">
          <div className="column">
            <FieldController
              control={control}
              name="roleSlugs"
              render={({ field, fieldState }) => (
                <DropdownAsync
                  {...field}
                  label="Roles"
                  multiple
                  selectedOptions={selectedRoleOptions}
                  setSelectedOptions={setSelectedRoleOptions}
                  isSearching={isRoleSearching}
                  onSearch={handleRoleSearch}
                  options={roles}
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: fieldState.invalid,
                    message: fieldState.error?.message ?? 'Please select a role',
                  }}
                  responsive
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="column">
            <FieldController
              control={control}
              name="managerSlugs"
              render={({ field, fieldState }) => (
                <DropdownAsync
                  {...field}
                  label="Managers"
                  multiple
                  selectedOptions={selectedManagerOptions}
                  setSelectedOptions={setSelectedManagerOptions}
                  isSearching={isManagerSearching}
                  onSearch={handleManagerSearch}
                  options={managers}
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: fieldState.invalid,
                    message: fieldState.error?.message ?? 'Please select a manager',
                  }}
                  responsive
                />
              )}
            />
          </div>
        </div>

        {!!departments?.length && (
          <Fragment>
            <FieldController
              control={control}
              name="departmentSlug"
              rules={new RequiredValidator()}
              render={({ field, fieldState }) => (
                <DropdownSelect
                  {...field}
                  label="Department"
                  options={departments.map(department => ({
                    value: department.slug,
                    label: department.name,
                    onClick: () => {
                      setDepartmentSlug(department.slug);
                      setValue('teamSlug', undefined);
                    },
                  }))}
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: fieldState.invalid,
                    message: fieldState.error?.message ?? 'Please select a department',
                  }}
                  responsive
                />
              )}
            />

            {!!teams?.length && (
              <FieldController
                control={control}
                name="teamSlug"
                rules={new RequiredValidator()}
                render={({ field, fieldState }) => (
                  <DropdownSelect
                    {...field}
                    label="Team"
                    options={teams
                      .filter(team => (!departmentSlug ? true : team.departmentSlug === departmentSlug))
                      .map(team => ({
                        value: team.slug!,
                        label: team.teamName,
                      }))}
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: fieldState.invalid,
                      message: fieldState.error?.message ?? 'Please select a team',
                    }}
                    responsive
                  />
                )}
              />
            )}
          </Fragment>
        )}

        {!!countries?.length && (
          <FieldController
            control={control}
            name="countryId"
            rules={new RequiredValidator()}
            render={({ field, fieldState }) => (
              <DropdownSearch
                {...field}
                label="Country"
                options={countries.map(country => ({
                  value: country.id!.toString(),
                  label: country.name,
                  icon: <FlagIcon isoCode={country.iso} />,
                }))}
                validation={{
                  dirty: fieldState.isDirty || !!fieldState.error,
                  invalid: fieldState.invalid,
                  message: fieldState.error?.message ?? 'Please select a country',
                }}
                responsive
              />
            )}
          />
        )}
        <FieldController control={control} name="languages" render={({ field }) => <DropdownSearch {...field} label="Languages" options={languages} responsive multiple />} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <Button type="button">Submit</Button>
        </div>
      </form>
    </div>
  );
}
